import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../providers/auth_provider';
import { useNavigate } from 'react-router-dom';

export default function AffiliateEarnings({ user_id }) {
    const [earnings, setEarnings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10);
    const { token } = useAuth();
    const navigate = useNavigate();

    const fetchAffiliateEarnings = async (userId, currentPage, searchTerm) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/affiliate/earnings/${userId}`,
                {
                    params: {
                        page: currentPage,
                        limit,
                        search: searchTerm
                    },
                    headers: {
                        Signature: process.env.REACT_APP_API_SIGNATURE,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setEarnings(response.data.data);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            const timeoutId = setTimeout(() => {
                fetchAffiliateEarnings(user_id, page, search);
            }, 500); // Debounce search
            return () => clearTimeout(timeoutId);
        }
    }, [token, page, search, user_id]);

    const totalEarnings = earnings.reduce((sum, e) => sum + e.earnings, 0);
    const directEarnings = earnings.filter(e => e.earning_from === 'direct_referral');
    const indirectEarnings = earnings.filter(e => e.earning_from === 'indirect_referral');
    const directTotal = directEarnings.reduce((sum, e) => sum + e.earnings, 0);
    const indirectTotal = indirectEarnings.reduce((sum, e) => sum + e.earnings, 0);

    const handleViewUserDashboard = (userId) => {
        navigate('/users/view', { state: { user_id: userId } });
        setEarnings([]);
        fetchAffiliateEarnings(userId, 1, '');
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1); // Reset to first page when searching
    };

    return (
        <>
            <div className="card card-action mb-6 shadow-sm">
                <div className="card-header text-white d-flex border-bottom justify-content-between align-items-center">
                    <h5 className="card-action-title mb-0">
                        <i className="ri-wallet-line ri-24px me-3" />
                        Affiliate Earnings Dashboard
                    </h5>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-light text-dark me-2">
                            Total: ${totalEarnings.toFixed(2)}
                        </span>
                    </div>
                </div>

                <div className="card-body">

                    <div className="row my-4">
                        <div className="col-6">
                            <div className="card bg-opacity-10 border-0">
                                <div className="card-body py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-1">Direct Earnings</h6>
                                            <small className="text-muted">
                                                {directEarnings.length} referral{directEarnings.length !== 1 ? 's' : ''}
                                            </small>
                                        </div>
                                        <h5 className="text-success mb-0">
                                            ${directTotal.toFixed(2)}
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="card bg-opacity-10 border-0">
                                <div className="card-body py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-1">Indirect Earnings</h6>
                                            <small className="text-muted">
                                                {indirectEarnings.length} referral{indirectEarnings.length !== 1 ? 's' : ''}
                                            </small>
                                        </div>
                                        <h5 className="text-primary mb-0">
                                            ${indirectTotal.toFixed(2)}
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-auto" style={{ maxHeight: '30rem', scrollbarWidth: "none" }}>
                            {/* Search Input */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name or email..."
                                    value={search}
                                    onChange={handleSearch}
                                    style={{ width: '300px' }}
                                />
                            </div>
                            {earnings.length > 0 ? (
                                <>
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Referred By</th>
                                                <th>Referred User</th>
                                                <th>Transaction Amount</th>
                                                <th>Earnings</th>
                                                <th>Purpose</th>
                                                <th>Earning Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {earnings.map((earning, index) => {
                                                const referredUser = earning.referred_user;
                                                const subscription = earning.earning_source;
                                                const transaction = subscription?.transactions;

                                                return (
                                                    <tr key={earning.id}>
                                                        <td>{(page - 1) * limit + index + 1}</td>
                                                        <td
                                                            className={referredUser?.referral_user?.id === user_id ? "" : "text-capitalize cursor-pointer text-primary"}
                                                            onClick={() => referredUser?.referral_user?.id === user_id ? null : handleViewUserDashboard(referredUser.referral_user.id)}
                                                        >
                                                            {referredUser.referral_user?.first_name || 'Unknown'} {referredUser.referral_user?.last_name || ''}
                                                        </td>
                                                        <td>
                                                            <strong className='text-capitalize'>
                                                                {referredUser?.first_name || 'Unknown'} {referredUser?.last_name || ''}
                                                            </strong>
                                                            <br />
                                                            <small>{referredUser?.email || 'No Email'}</small>
                                                        </td>
                                                        <td>
                                                            {transaction?.amount
                                                                ? `$${transaction.amount.toFixed(2)}`
                                                                : 'N/A'}
                                                        </td>
                                                        <td>${earning.earnings.toFixed(2)}</td>
                                                        <td>
                                                            {subscription?.plans?.name
                                                                ? subscription.plans.name
                                                                : earning.description || 'N/A'}
                                                        </td>
                                                        <td>
                                                            {earning.earning_from === 'direct_referral' ? (
                                                                <span className="badge bg-success">Direct</span>
                                                            ) : (
                                                                <span className="badge bg-primary">Indirect</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div className=" mt-4">
                                        <nav>
                                            <ul className="pagination">
                                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page - 1)}
                                                        disabled={page === 1}
                                                    >
                                                        <i className='tf-icon ri-skip-back-mini-line ri-20px'></i>
                                                    </button>
                                                </li>
                                                {[...Array(totalPages)].map((_, index) => (
                                                    <li
                                                        key={index + 1}
                                                        className={`page-item ${page === index + 1 ? 'active' : ''}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(index + 1)}
                                                        >
                                                            {index + 1}
                                                        </button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page + 1)}
                                                        disabled={page === totalPages}
                                                    >
                                                        <i className="tf-icon ri-skip-forward-mini-line ri-20px"></i>

                                                    </button>
                                                </li>
                                            </ul>
                                        </nav >
                                    </div >
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="ri-wallet-line ri-4x text-muted mb-3"></i>
                                    <h4 className="mb-3">No Earnings Found</h4>
                                    <p className="text-muted">
                                        {search ? 'No results match your search criteria.' : 'Your affiliate earnings will appear here.'}
                                    </p>
                                </div>
                            )
                            }
                        </div >
                    )}
                </div >
            </div >
        </>
    );
}